import React, { useEffect, useMemo, useState } from 'react';
import { getPestleArticlesByMineral } from '../services/pestleApi';

const DEFAULT_MINERALS = [
  'Graphite',
  'Lithium',
  'Cobalt',
  'Nickel',
  'Rare Earth Elements',
  'Critical minerals',
];

function parseDateToMs(dateValue) {
  if (!dateValue) return 0;
  const t = Date.parse(String(dateValue));
  return Number.isFinite(t) ? t : 0;
}

function dedupeByKey(items) {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    if (!item) continue;
    const key = `${item.message || ''}__${item.sourceUrl || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

export default function NewsTicker({
  minerals = DEFAULT_MINERALS,
  maxItems = 3,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        // Requirement: query analyzed-articles with `limit=1` per mineral.
        const results = await Promise.all(
          minerals.map((m) =>
            getPestleArticlesByMineral(m, 1).catch(() => [])
          )
        );

        const flattened = results.flat();
        const linkable = flattened
          .filter((a) => a && typeof a.source_url === 'string' && a.source_url.trim().length > 0)
          .map((a) => ({
            message: a.headline || a.title || 'News update',
            sourceUrl: a.source_url,
            sourceName: a.source_name || a.source || 'Source',
            dateMs: parseDateToMs(a.date),
            countries: Array.isArray(a.countries_involved) ? a.countries_involved : [],
          }));

        const hasIndiaPerspective = linkable.some((x) =>
          x.countries?.some((c) => String(c).toLowerCase().includes('india'))
        );
        const filtered = hasIndiaPerspective
          ? linkable.filter((x) =>
              x.countries?.some((c) => String(c).toLowerCase().includes('india'))
            )
          : linkable;

        // Sort by recency when date data is usable; otherwise preserve existing order.
        const anyDates = filtered.some((x) => x.dateMs > 0);
        const sorted = anyDates
          ? [...filtered].sort((a, b) => b.dateMs - a.dateMs)
          : filtered;

        const deduped = dedupeByKey(sorted).slice(0, maxItems);
        if (!cancelled) setItems(deduped);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load news');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [minerals, maxItems]);

  const tickerItems = useMemo(() => items, [items]);

  return (
    <div className="news-ticker-shell bg-gray-50 dark:bg-gray-800/60 border-b border-secondary-200 dark:border-gray-700">
      <div className="w-full px-4 py-2 flex items-center gap-4">
        <div className="shrink-0">
          <div className="text-xs font-semibold text-gray-900 dark:text-gray-200">
            Latest News
          </div>
          {/* <div className="text-[11px] text-gray-600 dark:text-gray-400 leading-tight">
            Top {maxItems} updates across Graphite, Lithium, Cobalt, Nickel &amp; Rare Earths
          </div> */}
        </div>

        <div className="min-w-0 flex-1 overflow-hidden">
          {loading ? (
            <div className="text-sm text-gray-700 dark:text-gray-300 truncate">
              Loading top critical-minerals news...
            </div>
          ) : error ? (
            <div className="text-sm text-amber-700 dark:text-amber-300 truncate">
              {error}
            </div>
          ) : tickerItems.length === 0 ? (
            <div className="text-sm text-gray-700 dark:text-gray-300 truncate">
              No recent critical mineral news available.
            </div>
          ) : (
            <div className="news-ticker" aria-label="Critical minerals news ticker">
              <div className="news-ticker-track">
                {/* Duplicate sequence for seamless marquee */}
                {[0, 1].map((copyIdx) => (
                  <div key={copyIdx} className="news-ticker-seq">
                    {tickerItems.map((it, idx) => (
                      <a
                        key={`${copyIdx}-${idx}-${it.sourceUrl}`}
                        href={it.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="news-ticker-item inline-flex items-center gap-2 max-w-[62vw] overflow-hidden text-ellipsis whitespace-nowrap rounded hover:text-primary-700 dark:hover:text-primary-300"
                        title={it.message}
                      >
                        <span className="text-gray-900 dark:text-gray-100 font-medium text-sm">
                          {it.message}
                        </span>
                        {it.sourceName ? (
                          <span className="text-gray-500 dark:text-gray-400 text-xs shrink-0">
                            ({it.sourceName})
                          </span>
                        ) : null}
                      </a>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

